import * as THREE from 'three'
import {
  generateNodeType,
  generateType
} from 'src/api/models/utils/generateNodeType'
import {
  coverPlateProperties,
  coverPlate,
  coverPlateEdgeProperties,
  coverPlateEdge,
  coverPlateConnection,
  coverProperties,
  cover,
  coverEdge
} from 'src/constants/typenames'
import { lookFrom, lookFromMatrices } from 'src/constants/matrices'
import { Color } from 'src/api/misc/Color'
import { coverColors, green } from 'src/constants/colors'
import { types, getParentOfType, Instance, getParent } from 'mobx-state-tree'
import { ElementConnection } from 'src/api/models/Elements'
import { EnclosureConnection } from 'src/api/models/Enclosure'
import { getSideTranslation, getSidePosition } from 'src/utils/getTranslation'
import { getLookAt } from 'src/api/models/utils/getLookAt'
import { getSideProperties } from 'src/utils/getSideProperties'
import { generateCoverPlateShapes } from 'src/vectorPaths/coverPlate'
import { getReferencePoints2D } from 'src/utils/getReferencePoints'
import {
  enclosingTopBottom,
  enclosingRightLeft,
  enclosingFrontBack
} from 'src/components/viewer/components/paths'
import {
  generateEnumReferenceValue,
  generateValueTypeWithLimits,
  Side
} from 'src/api/models/Values/index'
import { ReferencePoints } from 'src/constants/referencePoints'
import {
  ICoverEdge,
  ICover,
  ICoverProperties,
  ICoverPlate,
  ICoverPlateProperties,
  ICoverPlateEdge,
  ICoverPlateEdgeProperties,
  ICoverPlateConnection
} from './types'
import { IElementConnection } from '../../Elements/types'
import { IProject } from '../../Project/types'

const tl = new THREE.Vector3()
const tr = new THREE.Vector3()
const bl = new THREE.Vector3()

const CoverPlateProperties: ICoverPlateProperties = generateType(
  coverPlateProperties
)
  .props({
    thickness: types.optional(generateValueTypeWithLimits({}), {
      value: 3,
      unit: 'mm'
    })
  })
  .views((self) => {
    // const boundings = getParentOfType(self, EnclosureConnection()).adapterEdges[0].node
    const enclosure = getParentOfType(self, EnclosureConnection) as any
    // const adapterBoundings: THREE.Box3 = enclosure.adapterEdges[0].node.boundings
    const cover = getParentOfType(self, Cover) as any
    const coverPlateConnection = getParentOfType(
      self,
      CoverPlateConnection
    ) as any
    const coverPlateEdge = getParentOfType(self, CoverPlateEdge) as Instance<
      ICoverPlateEdge
    >
    // const properties = getSideProperties(coverPlateEdge.properties.side, adapterBoundings, {
    //   edges: coverPlateConnection.edges.filter(({ id }, i) => id !== coverPlateEdge.id)
    // })
    const siblings = {
      edges: coverPlateConnection.edges.filter(
        ({ id }: { id: string }) => id !== coverPlateEdge.id
      )
    }
    return {
      get color() {
        return cover.properties.color
      },
      get width() {
        return getSideProperties(
          coverPlateEdge.properties.side,
          enclosure.adapterEdges[0].node.boundings,
          siblings
        ).width
      },
      get length() {
        return getSideProperties(
          coverPlateEdge.properties.side,
          enclosure.adapterEdges[0].node.boundings,
          siblings
        ).length
      },
      get shape() {
        const side = coverPlateEdge.properties.side
        const { width, length } = getSideProperties(
          coverPlateEdge.properties.side,
          enclosure.adapterEdges[0].node.boundings,
          siblings
        )
        let path
        if (['top', 'bottom'].includes(side)) {
          path = enclosingTopBottom(
            width.value,
            length.value,
            self.thickness.value
          )
        } else if (['right', 'left'].includes(side)) {
          path = enclosingRightLeft(
            width.value,
            length.value,
            self.thickness.value
          )
        } else if (['front', 'back']) {
          path = enclosingFrontBack(
            width.value,
            length.value,
            self.thickness.value
          )
        }
        const points = path.map(
          ([x, y]: [number, number]) => new THREE.Vector2(x, y)
        )
        const shape = new THREE.Shape(points)
        return shape
      },
      get projectedPrintedcircuitboardReferencePoints() {
        const side = coverPlateEdge.properties.side
        const project = getParent<IProject>(self, 9)
        const pcbRP = project.content.edges[0].node.getReferencePoints(side)
        const platePosition = getSidePosition(side, cover.boundings)
        const dimension = pcbRP.centerCenter.findIndex((v) => v !== 0)
        const projectedReferencePoints = Object.entries(pcbRP).reduce(
          (prev, [key, value]) => {
            const newValue = value
            newValue[dimension] = platePosition[dimension]
            return { ...prev, [key]: newValue }
          },
          {}
        ) as typeof pcbRP
        return projectedReferencePoints
      },
      get projectedPrintedcircuitboardPlane() {
        const side = coverPlateEdge.properties.side
        const {
          topLeft,
          topRight,
          centerCenter,
          bottomLeft
        } = ((self as unknown) as Instance<ICoverPlateProperties>)
          .projectedPrintedcircuitboardReferencePoints as ReferencePoints<
          [number, number, number]
        >
        const tlV = tl.set(...topLeft)
        const width = tlV.distanceTo(tr.set(...topRight))
        const height = tlV.distanceTo(bl.set(...bottomLeft))
        const geometry = new THREE.PlaneBufferGeometry(width, height)
        geometry.applyMatrix4(lookFromMatrices[side])
        geometry.translate(...centerCenter)
        return geometry
      },
      get projectedPrintedcircuitboardVisible() {
        const project = getParent<IProject>(self, 9)
        const pcb = project.content.edges[0].node
        return pcb.focusedSide === coverPlateEdge.properties.side
      }
    }
  })

export const CoverPlate = (): ICoverPlate =>
  generateNodeType(coverPlate)
    .props({
      properties: CoverPlateProperties,
      elements: types.optional(
        types.late(ElementConnection) as IElementConnection,
        {} as any
      )
      // holes: types.optional(types.maybe(types.late(ElementConnection) as IElementConnection), {})
    })
    .views((self) => ({
      get boundings() {
        const x = self.properties.width.value
        const y = self.properties.thickness.value
        const z = self.properties.length.value
        return new THREE.Box3().setFromCenterAndSize(
          new THREE.Vector3(),
          new THREE.Vector3(x, y, z)
        )
      }
    }))
    .views((self) => ({
      getReferencePoints() {
        const x = self.properties.width.value / 2
        const y = self.properties.length.value / 2
        return getReferencePoints2D(
          new THREE.Box2(new THREE.Vector2(x, y), new THREE.Vector2(-x, -y))
        )
      }
    }))
    .views((self) => ({
      get referencePoints(): ReferencePoints<[number, number, number]> {
        const referencePoints2D = self.getReferencePoints()
        return Object.entries(referencePoints2D).reduce(
          (p, [key, value]) => ({ ...p, [key]: [value[1], 0, value[0]] }),
          {} as ReferencePoints<[number, number, number]>
        )
      }
    }))

const CoverPlateEdgeProperties: ICoverPlateEdgeProperties = generateType(
  coverPlateEdgeProperties
)
  .props({
    side: Side
  })
  .views((self) => {
    const cover: any = getParentOfType(self, Cover)
    return {
      get translation() {
        return getSideTranslation(self.side, cover.contentBoundings)
      }
    }
  })

export const CoverPlateEdge: ICoverPlateEdge = generateNodeType(coverPlateEdge)
  .props({
    node: types.late(CoverPlate),
    properties: CoverPlateEdgeProperties
  })
  .views((self) => {
    return {
      get lookAt() {
        return getLookAt(self.properties.side)
      }
    }
  })

const CoverPlateConnection: ICoverPlateConnection = generateType(
  coverPlateConnection
).props({
  edges: types.array(CoverPlateEdge)
})

const CoverProperties: ICoverProperties = generateType(coverProperties).props({
  color: generateEnumReferenceValue(Color, coverColors, green, 'CoverColor')
})

export const Cover: ICover = generateNodeType(cover)
  .props({
    //color: types.optional(types.reference(Color), green), //types.optional(types.enumeration(coverColors), green),
    plates: CoverPlateConnection,
    properties: types.optional(CoverProperties, {
      color: { value: green }
    } as any)
  })
  .views((self) => {
    const enclosure = getParentOfType(self, EnclosureConnection) as any
    return {
      get boundings() {
        return enclosure.adapterEdges[0].node.boundings
          .clone()
          .expandByVector(new THREE.Vector3(3, 3, 3))
      },
      get contentBoundings() {
        return enclosure.adapterEdges[0].node.boundings
      }
    }
  })

export const CoverEdge = (): ICoverEdge =>
  generateNodeType(coverEdge).props({
    node: Cover
  })
